import { useContext, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import ContentText from "../components/contentText.jsx";
import { MachinePreview } from "../components/machinePreview";
import { manufacturerLogo } from "../components/manufacturerLogos.js";
import Pagination from "../components/pagination";
import { reactSelectClassNames, reactSelectStyles } from "../components/reactSelectStyles";
import { MachineContext } from "../context/MachineContext";
import { useLocalePath } from "../hooks/useLocalePath";
import { absoluteUrl } from "../seoUtils";
import { categoryUrlSlug, machineCategoryUrlSlug, slugify } from "../slug.js";

const CATALOGUE_HEADING = "CNC-maskiner och snickerimaskiner";
const CATALOGUE_INTRO =
  "AS Maskiner är leverantör av CNC-maskiner och snickerimaskiner i Sverige och Norden. " +
  "Vi levererar maskiner för trä- och plastindustrin — plattuppdelningssågar, kantlistmaskiner, " +
  "CNC-bearbetningscenter, putsmaskiner, spånsug och automation — och hjälper till med leverans, " +
  "installation, service och finansiering.";

const MAX_ITEMS_PER_PAGE = 12;
// Cap the ItemList so a 90-machine catalogue doesn't bloat every page's head.
const MAX_SCHEMA_ITEMS = 50;

const splitParam = (value) => (value ? value.split(",").filter(Boolean) : []);

const Catalogue = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { machines, machineManufacturers, machineCategories } = useContext(MachineContext);
  const { locale, localePath } = useLocalePath();

  // Filters and the page number are derived from the URL rather than mirrored into state via
  // effects. Effects don't run during the server render, so an effect-initialised
  // catalogue rendered as an empty page for crawlers. Deriving also removes the "reset page
  // when the filter changes" effect — the clamp below handles it.
  const filters = useMemo(
    () => ({
      categories: splitParam(searchParams.get("category")),
      manufacturers: splitParam(searchParams.get("manufacturer")),
    }),
    [searchParams]
  );

  // `?category=` carries URL slugs now, but the links that went out before it did carry the
  // database slug — both spellings keep selecting the same machines.
  const filteredMachines = useMemo(
    () =>
      machines.filter(
        (machine) =>
          (filters.manufacturers.length === 0 ||
            filters.manufacturers.includes(machine.manufacturer)) &&
          (filters.categories.length === 0 ||
            filters.categories.includes(machineCategoryUrlSlug(machine)) ||
            filters.categories.includes(machine.categorySlug))
      ),
    [machines, filters]
  );

  const totalPageCount = Math.ceil(filteredMachines.length / MAX_ITEMS_PER_PAGE) || 1;
  const currentPage = Math.min(
    Math.max(1, Number.parseInt(searchParams.get("page"), 10) || 1),
    totalPageCount
  );
  const paginatedItems = filteredMachines.slice(
    (currentPage - 1) * MAX_ITEMS_PER_PAGE,
    currentPage * MAX_ITEMS_PER_PAGE
  );

  const handleFilterChange = (filterName, value) => {
    const newParams = new URLSearchParams(searchParams);
    const paramName = filterName === "categories" ? "category" : "manufacturer";

    if (value.length > 0) {
      newParams.set(paramName, value.join(","));
    } else {
      newParams.delete(paramName);
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  const handlePageChange = (pageNumber) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", pageNumber.toString());
    setSearchParams(newParams);

    window.scrollTo(0, 0);
  };

  const catalogueUrl = absoluteUrl(locale, "/catalogue");
  const itemListElement = filteredMachines.slice(0, MAX_SCHEMA_ITEMS).map((machine, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: absoluteUrl(locale, `/machines/${machine.id}`),
    name: machine.title,
  }));

  return (
    <>
      <Helmet>
        <title>CNC-maskiner &amp; snickerimaskiner – leverantör i Sverige | AS Maskiner</title>
        <meta
          name="description"
          content="AS Maskiner är leverantör av CNC-maskiner och snickerimaskiner i Sverige och Norden — plattuppdelningssågar, kantlistmaskiner från KDT, putsmaskiner, spånsug och automation. Se hela katalogen."
        />
        <meta
          name="keywords"
          content="AS Maskiner, CNC, Kantlistmaskiner, Sprutautomat, Fladderputs, Bredbandsputs, KDT, KDT CNC, HOMAG, HOLZ-HER, SCM, Biesse, KA MA, AES, Viet, Felder, Masterwood, Weinig, CNC-maskiner, snickerimaskiner, industriella maskiner, plattuppdelningssåg, listhyvel"
        />
        <meta property="og:url" content={catalogueUrl} />
        <meta
          property="og:title"
          content="Produktkatalog - CNC-maskiner och snickerimaskiner från ledande tillverkare"
        />
        <meta
          property="og:description"
          content="Sveriges största utbud av CNC-maskiner, kantlistmaskiner och sprutautomater från KDT, HOMAG, SCM, Biesse m.fl."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            url: catalogueUrl,
            name: "Produktkatalog",
            description:
              "Utforska vår omfattande katalog av högkvalitativa maskiner för trä- och plastindustrin, inklusive CNC, Kantlistmaskiner, Bredbandsputs m.m. från välkända fabrikat så som KDT, HOMAG, HOLZ-HER, SCM och Biesse. Hitta de perfekta lösningarna för dina behov med vårt breda urval, konkurrenskraftiga priser och expertstöd.",
            ...(itemListElement.length > 0
              ? {
                  mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: filteredMachines.length,
                    itemListElement,
                  },
                }
              : {}),
          })}
        </script>
      </Helmet>

      <div className="flex flex-col m-4 sm:m-8">
        <div className="mx-auto w-full max-w-7xl box-border">
          <h1 className="text-2xl sm:text-3xl font-bold mb-3">
            <ContentText contentKey="catalogue.heading" fallback={CATALOGUE_HEADING} />
          </h1>

          <p className="max-w-3xl text-text-body leading-relaxed mb-6">
            <ContentText contentKey="catalogue.intro" fallback={CATALOGUE_INTRO} />
          </p>

          <CategoryLinks categories={machineCategories} localePath={localePath} />
          <ManufacturerLinks manufacturers={machineManufacturers} localePath={localePath} />

          <CatalogueFilters
            filters={filters}
            handleFilterChange={handleFilterChange}
            machineManufacturers={machineManufacturers}
            machineCategories={machineCategories}
          />

          <div className="flex mb-4 mt-6">
            <strong className="sm:text-xl" data-testid="number-of-filtered-machines">
              {filteredMachines.length}
            </strong>
            <span className="ml-1">filtrerade maskiner</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 w-full">
            {paginatedItems.map((machine) => (
              <MachinePreview machine={machine} key={machine.id} sx="h-full" />
            ))}
          </div>
          {filteredMachines.length > MAX_ITEMS_PER_PAGE && (
            <Pagination
              currentPage={currentPage}
              totalPageCount={totalPageCount}
              onPageChange={handlePageChange}
              sx="mt-4 sm:mt-8"
            />
          )}
        </div>
      </div>
    </>
  );
};

/**
 * A plain link per category, straight to its landing page. Search engines follow these; the
 * react-select filters below them are for people who want to combine filters.
 */
function CategoryLinks({ categories, localePath }) {
  const withMachines = categories.filter((category) => category.amountOfRelatedMachines > 0);
  if (withMachines.length === 0) return null;

  return (
    <nav className="mb-4" aria-label="Kategorier">
      <h2 className="font-semibold mb-2 text-sm">
        <ContentText contentKey="catalogue.categories_heading" fallback="Kategorier" />
      </h2>
      <ul className="flex flex-wrap gap-2">
        {withMachines.map((category) => (
          <li key={category.slug}>
            <Link
              to={localePath(`/catalogue/${categoryUrlSlug(category)}`)}
              className="inline-flex min-h-11 items-center rounded-full border border-border-subtle bg-surface px-4 text-sm font-medium text-text-strong hover:border-primary hover:text-primary transition-colors"
            >
              {category.name}
              <span className="ml-2 text-xs text-text-muted">
                {category.amountOfRelatedMachines}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** The brand strip: one link per manufacturer in stock, logo where we have one. */
function ManufacturerLinks({ manufacturers, localePath }) {
  const inStock = manufacturers.filter(
    (manufacturer) => manufacturer.amountOfRelatedMachines > 0 && manufacturer.name
  );
  if (inStock.length === 0) return null;

  return (
    <nav className="mb-6" aria-label="Fabrikat">
      <h2 className="font-semibold mb-2 text-sm">
        <ContentText contentKey="catalogue.manufacturers_heading" fallback="Fabrikat" />
      </h2>
      <ul className="flex flex-wrap gap-2">
        {inStock.map((manufacturer) => {
          const logo = manufacturerLogo(manufacturer.name);
          return (
            <li key={manufacturer.name}>
              <Link
                to={localePath(`/manufacturers/${slugify(manufacturer.name)}`)}
                className="inline-flex min-h-11 items-center gap-2 rounded-full border border-border-subtle bg-surface px-4 text-sm font-medium text-text-strong hover:border-primary hover:text-primary transition-colors"
                data-testid={`manufacturer-link-${slugify(manufacturer.name)}`}
              >
                {logo ? (
                  <img src={logo} alt={manufacturer.name} className="h-4 object-contain" />
                ) : (
                  manufacturer.name
                )}
                <span className="text-xs text-text-muted">
                  {manufacturer.amountOfRelatedMachines}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CatalogueFilters({
  filters,
  handleFilterChange,
  machineManufacturers,
  machineCategories,
}) {
  return (
    <div className="flex flex-wrap gap-4">
      <div className="flex-1 min-w-48">
        <h4 className="font-semibold mb-2 text-sm">Kategori</h4>
        <Select
          // react-select numbers its instances from a module-level counter to build the ids it
          // puts on the input and the aria attributes. On the server that counter keeps
          // climbing across requests while the browser's always starts at 1, so without a fixed
          // instanceId the ids in the server markup never match the ones hydration produces.
          instanceId="catalogue-category-filter"
          onChange={(options) =>
            handleFilterChange(
              "categories",
              options.map((option) => option.value)
            )
          }
          classNames={reactSelectClassNames}
          styles={reactSelectStyles}
          value={filters.categories.map((selected) => {
            const match = machineCategories.find(
              (item) => categoryUrlSlug(item) === selected || item.slug === selected
            );
            return { label: match?.name || selected, value: match ? categoryUrlSlug(match) : selected };
          })}
          isMulti={true}
          placeholder="Välj kategori"
          noOptionsMessage={() => "Inga resultat"}
          options={machineCategories.map((category) => ({
            label: category.name,
            value: categoryUrlSlug(category),
          }))}
        />
      </div>
      <div className="flex-1 min-w-48">
        <h4 className="font-semibold mb-2 text-sm">Fabrikat</h4>
        <Select
          instanceId="catalogue-manufacturer-filter"
          onChange={(options) =>
            handleFilterChange(
              "manufacturers",
              options.map((option) => option.value)
            )
          }
          classNames={reactSelectClassNames}
          styles={reactSelectStyles}
          value={filters.manufacturers.map((manufacturer) => ({
            label: manufacturer,
            value: manufacturer,
          }))}
          isMulti={true}
          placeholder="Välj fabrikat"
          noOptionsMessage={() => "Inga resultat"}
          options={machineManufacturers.map((manufacturer) => ({
            label: manufacturer.name,
            value: manufacturer.name,
          }))}
        />
      </div>
    </div>
  );
}

export default Catalogue;
